import { useCallback } from "react";

import { nodeTitle } from "@/features/caseFiles/nodeNames";

import { useCaseDataOptional } from "../data/CaseDataContext";
import { cardTabTitle } from "../files/fileCardSummary";
import { WORKFLOWS_TAB_TITLE } from "../openArtifacts";
import { artifactKey, type ArtifactKind } from "../types";

/** What `useTitleFor` answers when it does not know the artifact. */
export const FALLBACK: Record<ArtifactKind, string> = {
  file: "File",
  card: "Card",
  draft: "Draft",
  packet: "Filing Packet",
  form: "Form",
  settings: "Case Settings",
  plan: "Plan",
  key_facts: "Case Insights",
  time: "Time",
  company: "Company",
  workflows: WORKFLOWS_TAB_TITLE
};

/**
 * Best-effort title for an artifact the agent refers to by kind and id.
 * Outside the Case IDE (no case data) it falls back to the kind's label.
 */
export const useTitleFor = () => {
  const caseData = useCaseDataOptional();
  const nodes = caseData?.fs.nodes;
  const sections = caseData?.workspace.sections;
  return useCallback(
    (a: { kind: ArtifactKind; id: string; title?: string | null }): string => {
      if (a.title) return a.title;
      if (a.kind === "file" || a.kind === "card") {
        const node = nodes?.find((n) => n.id === a.id);
        const name = node ? nodeTitle(node) : null;
        if (a.kind === "file") return name ?? FALLBACK.file;
        return name ? cardTabTitle(name) : FALLBACK.card;
      }
      const key = artifactKey(a);
      for (const s of sections ?? []) {
        const item = s.items.find((i) => artifactKey(i.artifact) === key);
        if (item) return item.artifact.title;
      }
      return FALLBACK[a.kind];
    },
    [nodes, sections]
  );
};
